import { useCallback, useState } from 'react';
import { calculateTotalAttachmentSize } from 'libs/utils/fileUtils';
import { formatFileSize } from 'utils/formatFileSize';
import { MAX_ATTACHMENT_SIZE_BYTES, MAX_ATTACHMENT_SIZE_MB } from 'constants/canmail';
import type { EmailAttachment } from '../types';

export interface UseAttachmentsResult {
  attachments: EmailAttachment[];
  setAttachments: React.Dispatch<React.SetStateAction<EmailAttachment[]>>;
  /** 'edit' opens the modal in manage-existing mode; 'create' in upload mode. */
  modalMode: 'create' | 'edit';
  setModalMode: (mode: 'create' | 'edit') => void;
  /** Append newly uploaded attachments (create mode). */
  appendAttachments: (added: EmailAttachment[]) => void;
  /** Remove one by position (create mode chip delete). */
  removeAt: (index: number) => void;
  /** Seed both current and original lists when loading a scheduled email. */
  loadExisting: (loaded: EmailAttachment[]) => void;
  /** Restore the list captured by `loadExisting` — used when the user cancels. */
  revertToOriginal: () => void;
  /**
   * Validate the total size.
   * @returns an error message, or null when within the limit.
   */
  validateSize: () => string | null;
  reset: () => void;
}

/**
 * Owns the composer's attachment list.
 *
 * Note there is no add/delete staging here: `AttachmentModal` hands back the complete
 * desired list, which is sent verbatim in a single update request. See Phase 2 — the
 * index-based staging this replaced was deleting the wrong files.
 */
export function useAttachments(): UseAttachmentsResult {
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  // Snapshot taken when a scheduled email is loaded, so cancelling an edit restores it.
  const [originalAttachments, setOriginalAttachments] = useState<EmailAttachment[]>([]);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  const appendAttachments = useCallback((added: EmailAttachment[]) => {
    setAttachments((prev) => [...prev, ...added]);
  }, []);

  const removeAt = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const loadExisting = useCallback((loaded: EmailAttachment[]) => {
    setAttachments(loaded);
    setOriginalAttachments(loaded);
  }, []);

  const revertToOriginal = useCallback(() => {
    setAttachments(originalAttachments);
  }, [originalAttachments]);

  const validateSize = useCallback((): string | null => {
    if (!attachments || attachments.length === 0) return null;

    const totalSize = calculateTotalAttachmentSize(attachments);
    if (totalSize > MAX_ATTACHMENT_SIZE_BYTES) {
      return (
        `Total attachment size (${formatFileSize(totalSize)}) exceeds ${MAX_ATTACHMENT_SIZE_MB}MB limit. ` +
        'Please remove some attachments before sending.'
      );
    }
    return null;
  }, [attachments]);

  const reset = useCallback(() => {
    setAttachments([]);
    setOriginalAttachments([]);
    setModalMode('create');
  }, []);

  return {
    attachments,
    setAttachments,
    modalMode,
    setModalMode,
    appendAttachments,
    removeAt,
    loadExisting,
    revertToOriginal,
    validateSize,
    reset,
  };
}

export default useAttachments;
